import { useAccountSite, type Site } from "./siteDetails";

/** Header shown when text messaging isn't activated for the site (FOG-1762). */
export const SMS_NOT_ENABLED_TITLE = "Text not activated";

/** Body shown when text messaging isn't activated for the site (FOG-1762). */
export const SMS_NOT_ENABLED_REASON =
  "Contact your Queen One representative to get text messaging set up for this site.";

/** Header shown when RCS isn't enabled for the site (FOG-1764). */
export const RCS_NOT_ENABLED_TITLE =
  "RCS isn't enabled for this site — all sends will use the SMS version";

/** Body shown when RCS isn't enabled for the site (FOG-1764). */
export const RCS_NOT_ENABLED_REASON =
  "You can still build your RCS message below, but until RCS is approved, every recipient receives the SMS fallback. Contact your Queen One rep to get RCS approved.";

/**
 * True when a site has text messaging activated (SMS registered + approved with
 * Infobip). Pure so it can be used outside React; components should prefer
 * `useIsTextMessagingActive`.
 */
export function isTextMessagingActive(
  site: Pick<Site, "sms_enabled"> | null | undefined,
): boolean {
  return site?.sms_enabled === true;
}

/** Component hook: reads the current site and resolves the text-activation gate. */
export function useIsTextMessagingActive(siteId: string): boolean {
  const { data: site } = useAccountSite(siteId);
  return isTextMessagingActive(site);
}

/**
 * True when the site is RCS-approved. The `rcs_enabled` field is optional on the
 * site response because the backend hasn't rolled it out everywhere yet — a
 * missing field reads as "not approved." Pure so it can be used outside React;
 * components should prefer `useCanUseRcs`.
 */
export function canUseRcs(
  site: Pick<Site, "rcs_enabled"> | null | undefined,
): boolean {
  return site?.rcs_enabled === true;
}

/** Component hook: reads the current site and resolves the RCS-approval gate. */
export function useCanUseRcs(siteId: string): boolean {
  const { data: site } = useAccountSite(siteId);
  return canUseRcs(site);
}
